"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Check } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BillingToggle } from "@/components/BillingToggle";
import { HOBBY_FEATURE_ICONS, type PlanFeatureRow } from "@/lib/plan-features";

export interface PlanPriceInfo {
  id: string;
  unitAmount: number | null;
  currency: string;
}

export type PlanBilling = "monthly" | "annual";

export interface HobbyCtaContext {
  priceId: string | undefined;
  billing: PlanBilling;
}

export interface TrialCtaContext {
  priceId: string | undefined;
}

interface PlanCardsProps {
  monthly?: PlanPriceInfo;
  yearly?: PlanPriceInfo;
  trialPrice?: PlanPriceInfo | null;
  defaultBilling?: PlanBilling;
  onBillingChange?: (billing: PlanBilling) => void;
  toggleSize?: "sm" | "md";
  /** Optional content rendered centered between the toggle and the cards. */
  aboveCards?: ReactNode;
  /** Landing passes a Link CTA, the paywall passes a checkout Button. */
  renderHobbyCta: (ctx: HobbyCtaContext) => ReactNode;
  renderTrialCta: (ctx: TrialCtaContext) => ReactNode;
}

function formatPrice(unitAmount: number | null, currency: string) {
  if (unitAmount == null) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(unitAmount / 100);
}

function formatPerMonth(unitAmountYearly: number | null, currency: string) {
  // Yearly / 12 is often fractional cents ($100/yr → $8.33/mo). Always show
  // 2 decimals so prices are exact to the cent.
  if (unitAmountYearly == null) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(unitAmountYearly / 12 / 100);
}

function FeatureList({ features }: { features: PlanFeatureRow[] }) {
  const t = useTranslations("pricing");
  const categories = [
    { key: "Usage", label: t("categoryUsage") },
    { key: "Input Sources", label: t("categoryInputSources") },
    { key: "Audio & Voice", label: t("categoryAudioVoice") },
    { key: "Support", label: t("categorySupport") },
  ] as const;

  const grouped = categories
    .map((cat) => ({
      label: cat.label,
      items: features.filter((f) => f.category === cat.key),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
      {grouped.map((group) => (
        <div key={group.label}>
          <p className="text-[10px] font-semibold text-foreground/50 uppercase tracking-widest mb-2.5">
            {group.label}
          </p>
          <ul className="space-y-2.5">
            {group.items.map((feature) => (
              <li key={feature.text} className="flex items-start gap-2.5 group/item">
                <div className="bg-accent/10 rounded-full p-1 mt-0.5 shrink-0 transition-colors group-hover/item:bg-accent/20">
                  <feature.icon className="w-3 h-3 text-accent" />
                </div>
                <div className="flex flex-col">
                  <span className="text-foreground/90 text-sm font-medium">
                    {feature.text}
                  </span>
                  {feature.highlight && (
                    <span className="text-accent text-[10px] font-bold uppercase tracking-wide">
                      {feature.highlight}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * Shared pricing cards (one-time trial + Hobby), used by the landing
 * (`Pricing`) and the paywall (`StartTrialContent`). Copy comes from the
 * `pricing` i18n namespace in both contexts. Only the CTAs differ and are
 * injected via render props.
 *
 * NOTE: `trialFeat1Text` hard-codes the trial allowance ("3 transcripts +
 * 1 audio"). Keep it in sync with TRIAL_TRANSCRIPT_LIMIT /
 * TRIAL_AUDIO_LIMIT in `lib/usage-constants.ts` (and across all 15
 * `messages/*.json` locales).
 */
export function PlanCards({
  monthly,
  yearly,
  trialPrice,
  defaultBilling = "monthly",
  onBillingChange,
  toggleSize = "md",
  aboveCards,
  renderHobbyCta,
  renderTrialCta,
}: PlanCardsProps) {
  const t = useTranslations("pricing");
  const [billing, setBilling] = useState<PlanBilling>(defaultBilling);

  const handleBillingChange = (next: PlanBilling) => {
    setBilling(next);
    onBillingChange?.(next);
  };

  const activePrice = billing === "monthly" ? monthly : yearly;
  const displayPrice =
    billing === "monthly"
      ? formatPrice(monthly?.unitAmount ?? null, monthly?.currency ?? "usd")
      : formatPerMonth(yearly?.unitAmount ?? null, yearly?.currency ?? "usd");

  const currency = monthly?.currency ?? yearly?.currency ?? "usd";
  const monthlyPerMonthLabel =
    monthly?.unitAmount != null
      ? formatPrice(monthly.unitAmount, currency)
      : null;
  const annualPerMonthLabel =
    yearly?.unitAmount != null
      ? formatPerMonth(yearly.unitAmount, currency)
      : null;
  const yearlyTotalLabel =
    yearly?.unitAmount != null
      ? formatPrice(yearly.unitAmount, currency)
      : null;
  const monthlyYearlyTotalLabel =
    monthly?.unitAmount != null
      ? formatPrice(monthly.unitAmount * 12, currency)
      : null;
  // Only show savings when annual is actually cheaper — guards against
  // misconfigured Stripe prices (e.g. $100/yr vs $8/mo = $96/yr).
  const annualSavingsValue =
    monthly?.unitAmount != null && yearly?.unitAmount != null
      ? monthly.unitAmount * 12 - yearly.unitAmount
      : null;
  const annualSavingsLabel =
    annualSavingsValue != null && annualSavingsValue > 0
      ? formatPrice(annualSavingsValue, currency)
      : null;
  const hasSavings = annualSavingsLabel != null;
  const savingsText = hasSavings
    ? t("saveVsYear", { amount: annualSavingsLabel })
    : null;

  const features: PlanFeatureRow[] = [
    { icon: HOBBY_FEATURE_ICONS.feat1, text: t("feat1Text"), highlight: t("feat1Highlight"), category: "Usage" },
    { icon: HOBBY_FEATURE_ICONS.feat2, text: t("feat2Text"), category: "Usage" },
    { icon: HOBBY_FEATURE_ICONS.feat3, text: t("feat3Text"), category: "Input Sources" },
    { icon: HOBBY_FEATURE_ICONS.feat4, text: t("feat4Text"), highlight: t("feat4Highlight"), category: "Audio & Voice" },
    { icon: HOBBY_FEATURE_ICONS.feat9, text: t("feat9Text"), highlight: t("feat9Highlight"), category: "Audio & Voice" },
    { icon: HOBBY_FEATURE_ICONS.feat5, text: t("feat5Text"), highlight: t("feat5Highlight"), category: "Audio & Voice" },
    { icon: HOBBY_FEATURE_ICONS.feat7, text: t("feat7Text"), highlight: t("feat7Highlight"), category: "Audio & Voice" },
    { icon: HOBBY_FEATURE_ICONS.feat6, text: t("feat6Text"), category: "Support" },
    { icon: HOBBY_FEATURE_ICONS.feat8, text: t("feat8Text"), highlight: t("feat8Highlight"), category: "Support" },
  ];

  const trialFeatures = trialPrice
    ? [
        t("trialFeat1Text"),
        t("trialFeat2Text"),
        t("trialFeat5Text"),
        t("trialFeat3Text"),
        t("trialFeat4Text"),
      ]
    : [];

  return (
    <div>
      <div className="flex justify-center mb-8">
        <BillingToggle
          billing={billing}
          onChange={handleBillingChange}
          monthlyLabel={t("billingMonthly")}
          annualLabel={t("billingAnnual")}
          monthlyPriceLabel={monthlyPerMonthLabel}
          annualPerMonthLabel={annualPerMonthLabel}
          savingsText={savingsText}
          size={toggleSize}
        />
      </div>

      {aboveCards ? <div className="mb-6">{aboveCards}</div> : null}

      <div
        className={
          trialPrice
            ? "grid gap-6 md:grid-cols-2 items-stretch"
            : "mx-auto max-w-xl"
        }
      >
        {trialPrice && (
          <Card className="relative flex flex-col bg-card/50 backdrop-blur-md border border-border transition-all duration-500 hover:shadow-xl overflow-hidden">
            <div className="absolute top-0 right-0">
              <div className="bg-muted text-muted-foreground text-[10px] font-bold px-4 py-1 rounded-bl-xl uppercase tracking-wider">
                {t("trialCardBadge")}
              </div>
            </div>

            <CardHeader className="pt-8 pb-6">
              <div className="space-y-1">
                <h3 className="text-2xl font-bold text-foreground">{t("trialPlanName")}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t("trialPlanDescription")}
                </p>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col space-y-8">
              <div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-5xl font-black text-foreground tracking-tight">
                    {formatPrice(trialPrice.unitAmount, trialPrice.currency)}
                  </span>
                  <span className="text-muted-foreground font-medium text-sm">
                    {t("oneTime")}
                  </span>
                </div>
              </div>

              <ul className="space-y-2.5">
                {trialFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5">
                    <div className="bg-accent/10 rounded-full p-1 mt-0.5 shrink-0">
                      <Check className="w-3 h-3 text-accent" />
                    </div>
                    <span className="text-foreground/90 text-sm font-medium">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-4">
                {renderTrialCta({ priceId: trialPrice.id })}
                <p className="text-center text-xs text-muted-foreground mt-3">
                  {t("trialNote")}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="group relative flex flex-col bg-card/50 backdrop-blur-md border-2 border-accent shadow-accent/5 ring-4 ring-accent/5 transition-all duration-500 hover:shadow-2xl overflow-hidden">
          <div className="absolute top-0 right-0">
            <div className="bg-accent text-accent-foreground text-[10px] font-bold px-4 py-1 rounded-bl-xl uppercase tracking-wider">
              {t("trialBadge")}
            </div>
          </div>

          <CardHeader className="pt-8 pb-6">
            <div className="space-y-1">
              <h3 className="text-2xl font-bold text-foreground">{t("planName")}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t("planDescription")}
              </p>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col space-y-8">
            <div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-5xl font-black text-foreground tracking-tight">
                  {displayPrice}
                </span>
                <div className="flex flex-col">
                  <span className="text-muted-foreground font-medium text-sm">
                    {t("perMonth")}
                  </span>
                  {billing === "annual" && (
                    <span className="text-muted-foreground text-[10px] font-bold uppercase tracking-tighter">
                      {t("billedAnnually")}
                    </span>
                  )}
                </div>
              </div>
              {billing === "annual" && yearlyTotalLabel && (
                <p className="mt-2 text-sm">
                  <span className="font-bold text-foreground">
                    {t("yearlyTotal", { total: yearlyTotalLabel })}
                  </span>
                  {hasSavings && monthlyYearlyTotalLabel && (
                    <span className="ml-2 text-muted-foreground line-through">
                      {t("wasTotal", { total: monthlyYearlyTotalLabel })}
                    </span>
                  )}
                </p>
              )}
            </div>

            <FeatureList features={features} />

            <div className="mt-auto pt-4">
              {renderHobbyCta({ priceId: activePrice?.id, billing })}
              <p className="text-center text-xs text-muted-foreground mt-3">
                {t("noChargeNote")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
